import React, { useEffect, useState } from 'react';

import { listActivityPlugins, GAME_MODE_CATALOG, type GameMode, type RoomPhase } from '@interactive-room';
import { isLiveHostMode } from '../../studio/constants/liveHostModes';

import type { ActivityPreset } from '@interactive-room/catalog/activityPresets';

import { ModeCard } from './ModeCard';

import { ActivityModeCatalog } from './ActivityModeCatalog';

import { StudioFlashDialog } from './StudioFlashDialog';

import type { ActivityDraft } from '../../studio/activityStorage';

import {

  Sheet,

  SheetContent,

  SheetDescription,

  SheetHeader,

  SheetTitle,

} from './primitives/Sheet';



type SheetView = 'modes' | 'catalog';



interface ActivitySwitcherSheetProps {

  open: boolean;

  onOpenChange: (open: boolean) => void;

  activeMode?: GameMode | null;

  phase: RoomPhase;

  stagedDraftId?: string | null;

  subjectId?: string;

  onBrowseMode: (mode: GameMode) => void;

  onStageDraft: (draft: ActivityDraft) => void;

  onStagePreset: (preset: ActivityPreset) => void;

  onLaunchStaged: () => void;

  stagedTitle?: string | null;

}



export function ActivitySwitcherSheet({

  open,

  onOpenChange,

  activeMode,

  phase,

  stagedDraftId,

  subjectId,

  onBrowseMode,

  onStageDraft,

  onStagePreset,

  onLaunchStaged,

  stagedTitle,

}: ActivitySwitcherSheetProps) {

  const [view, setView] = useState<SheetView>('modes');

  const [catalogMode, setCatalogMode] = useState<GameMode | null>(null);

  const [flashOpen, setFlashOpen] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState<string | undefined>(undefined);

  const [pendingMode, setPendingMode] = useState<GameMode | null>(null);

  const [catalogRefresh, setCatalogRefresh] = useState(0);



  const liveSession = phase === 'ACTIVE';



  useEffect(() => {

    if (!open) {

      setView('modes');

      setCatalogMode(null);

      setPendingMode(null);

      setEditingDraftId(undefined);

    }

  }, [open]);



  const openCatalog = (mode: GameMode) => {

    if (liveSession && mode !== activeMode) {

      setPendingMode(mode);

      return;

    }

    setCatalogMode(mode);

    setView('catalog');

    onBrowseMode(mode);

  };



  const confirmSwitchToCatalog = () => {

    if (pendingMode) {

      setCatalogMode(pendingMode);

      setView('catalog');

      onBrowseMode(pendingMode);

      setPendingMode(null);

    }

  };



  const handleEditDraft = (draft: ActivityDraft) => {
    if (!catalogMode) return;
    setEditingDraftId(draft.id);
    setFlashOpen(true);
  };



  return (

    <>

      <Sheet open={open} onOpenChange={onOpenChange}>

        <SheetContent side="right" className="sala-sheet--wide sala-sheet--stacked">

          <SheetHeader>

            <SheetTitle>

              {view === 'modes' ? 'Actividades' : GAME_MODE_CATALOG[catalogMode!]?.displayName}

            </SheetTitle>

            <SheetDescription>
              {view === 'modes'
                ? 'Elige una modalidad y selecciona un borrador guardado para transmitir.'
                : 'Selecciona una actividad guardada o prepárala en Studio.'}
            </SheetDescription>

          </SheetHeader>



          {pendingMode && (

            <div className="sala-sheet__alert">

              <p className="text-xs leading-relaxed text-amber-200/90">

                ¿Interrumpir la actividad en curso y abrir otra modalidad?

              </p>

              <div className="mt-2 flex gap-2">

                <button type="button" className="sala-btn-hero py-1.5 text-[10px]" onClick={confirmSwitchToCatalog}>

                  Sí, continuar

                </button>

                <button

                  type="button"

                  className="sala-btn-outline py-1.5 text-[10px]"

                  onClick={() => setPendingMode(null)}

                >

                  Cancelar

                </button>

              </div>

            </div>

          )}



          <div className={`sala-sheet__stack min-h-0 flex-1${view === 'catalog' ? ' sala-sheet__stack--catalog' : ''}`}>

            <div className="sala-sheet__panel">

              <div className="sala-sheet__body">

                <section className="sala-sheet__section">

                  <p className="sala-label mb-2">Modalidad</p>

                  <div className="sala-mode-rail">

                    {listActivityPlugins()
                      .filter((plugin) => isLiveHostMode(plugin.mode))
                      .map((plugin) => (
                      <ModeCard

                        key={plugin.mode}

                        mode={plugin.mode}

                        selected={activeMode === plugin.mode && view === 'modes'}

                        onSelect={() => openCatalog(plugin.mode)}

                        layout="rail"

                      />

                    ))}

                  </div>

                </section>

              </div>

            </div>



            {catalogMode && (

              <div className={`sala-sheet__panel${view === 'catalog' ? ' sala-sheet__panel--active' : ''}`}>

                <div className="sala-sheet__body sala-sheet__body--catalog">

                  <ActivityModeCatalog
                    key={`${catalogMode}-${catalogRefresh}`}
                    mode={catalogMode}
                    subjectId={subjectId}
                    stagedId={stagedDraftId}
                    onBack={() => setView('modes')}
                    onEditDraft={handleEditDraft}
                    onStageDraft={onStageDraft}
                    onStagePreset={onStagePreset}
                  />

                </div>

              </div>

            )}

          </div>



          {stagedTitle && view === 'catalog' && (

            <div className="sala-sheet__footer">

              <button type="button" className="sala-btn-hero w-full py-2.5 text-xs" onClick={onLaunchStaged}>

                Lanzar «{stagedTitle}»

              </button>

            </div>

          )}

        </SheetContent>

      </Sheet>



      {catalogMode && editingDraftId && (
        <StudioFlashDialog
          open={flashOpen}
          onOpenChange={(next) => {
            setFlashOpen(next);
            if (!next) setEditingDraftId(undefined);
          }}
          mode={catalogMode}
          draftId={editingDraftId}
          onSaved={(draft) => {
            setCatalogRefresh((n) => n + 1);
            onStageDraft(draft);
            setEditingDraftId(undefined);
          }}
        />
      )}

    </>

  );

}

